/**
 * Start-of-day briefing: when an operator starts work, show what is still waiting at their
 * facilities (online orders in WIP, Try at Home pickups not yet scanned) so they start on it.
 *
 * Pure rules, no browser APIs: when a briefing is due, each facility's start time, and the backlog
 * built from the two list APIs the Hub pages already use. Push alerts keep covering new work
 * 08:00-22:00; the briefing covers what is already waiting.
 */
import dayjs from 'dayjs';
import {
  DELIVERY_LABELS,
  type DeliverySchedulePackage,
  deliveryScheduleState,
  resolveDeliverySchedule,
} from '@admin-panel/pages/orders/DeliveryFollowUps/presentation';
import { ORDER_PICKING_PATH, STN_PICKING_PATH } from './hubAlertRules';

/** India Standard Time has no daylight saving: a fixed offset is exact. */
const IST_OFFSET_MINUTES = 330;
const MINUTES_PER_DAY = 24 * 60;
const MINUTE_MS = 60_000;

/** The working day starts at 05:00 IST: a 07:00 arrival is briefed, a 23:00 reopen is not. */
export const SHIFT_DAY_START = '05:00';
/** Start time for facilities without express timings. */
export const DEFAULT_START_TIME = '10:30';
/** One page per list; a full page reads "50+" and the card says counts may be partial. */
export const BRIEFING_PAGE_SIZE = 50;

export const TRY_AT_HOME_USE_CASE = 'TRY_AT_HOME_CONSOLIDATION';
export const OPEN_PICKUP_STATUSES = ['OPEN', 'IN_PROGRESS'];
const HUB_FACILITY_TYPES = ['STORE', 'WAREHOUSE'];
const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** `restore`: show again (fresh counts, no chime) a briefing left open before a reload. */
export type BriefingTrigger =
  | 'login'
  | 'open'
  | 'activity'
  | 'start'
  | 'manual'
  | 'restore'
  /** Refresh in the alerts drawer: reload now, quietly. */
  | 'refresh';

export type BriefingFacility = {
  id: number;
  name?: string | null;
  facilityType?: string | null;
  expressServiceabilityEnabled?: boolean | null;
  config?: Record<string, unknown> | null;
};

export type BriefingPackage = {
  id: number;
  fulfillmentFacilityId?: number | null;
  currentStatus?: string | null;
  deliveryMode?: string | null;
  fulfillmentMode?: string | null;
  isTestOrder?: boolean | null;
  isExpress?: boolean | null;
  tryAtHome?: boolean | null;
  /** The customer's requested date/time (CUSTOMER_REQUESTED_DELIVERY_DATE/_TIME) wins over the ETA. */
  courierMetadata?: Record<string, string | undefined> | null;
  promisedDeliveryDate?: string | null;
  estimatedDeliveryDate?: string | null;
};

export type BriefingPickup = {
  id: number;
  fromFacilityId?: number | null;
  status?: string | null;
  useCase?: string | null;
  totalItemsPendingToBeScanned?: number | null;
  expectedScanDate?: string | number | null;
};

export type FacilityBacklog = {
  facilityId: number;
  facilityName: string;
  orders: number;
  ordersOverdue: number;
  ordersDueToday: number;
  pickups: number;
  pickupsOverdue: number;
  pickupsDueToday: number;
  /** Scan-by after today (the Upcoming tab); the rest have no scan-by time. */
  pickupsUpcoming: number;
  /** The list came back full (BRIEFING_PAGE_SIZE rows): the count is a floor, shown as "50+". */
  ordersCapped?: boolean;
  pickupsCapped?: boolean;
};

/**
 * A facility where SCM runs picking: the orders the viewer can pick now (see `pickableOrders`), which replace the
 * WIP package count there. `packageIds` give the due dates of the ones known.
 */
export type PickableAtFacility = { count: number; packageIds: Set<string> };

export type Briefing = {
  /** Facilities with something waiting, busiest first. */
  facilities: Array<FacilityBacklog>;
  /** A list came back full, so counts may be partial. */
  partial: boolean;
  at: number;
};

const toMinutes = (hhmm: string) => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return hours * 60 + minutes;
};

const istMinuteOfDay = (now: number) =>
  (((Math.floor(now / MINUTE_MS) + IST_OFFSET_MINUTES) % MINUTES_PER_DAY) + MINUTES_PER_DAY) %
  MINUTES_PER_DAY;

/** The most recent instant at `hhmm` IST at or before `now`. */
const latestAt = (now: number, hhmm: string) => {
  const since = (istMinuteOfDay(now) - toMinutes(hhmm) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return Math.floor(now / MINUTE_MS) * MINUTE_MS - since * MINUTE_MS;
};

/** Start of the current working day (05:00 IST). */
export const shiftDayStart = (now: number) => latestAt(now, SHIFT_DAY_START);

/** The next instant strictly after `now` at `hhmm` IST. */
export const nextOccurrence = (now: number, hhmm: string) => {
  const at = latestAt(now, hhmm) + MINUTES_PER_DAY * MINUTE_MS;
  return at - now > MINUTES_PER_DAY * MINUTE_MS ? at - MINUTES_PER_DAY * MINUTE_MS : at;
};

/** End of today (IST), for "due today". */
const endOfIstDay = (now: number) => latestAt(now, '00:00') + MINUTES_PER_DAY * MINUTE_MS;

/** Express opening time when express is on and the time is valid, else 10:30. */
export const facilityStartTime = (facility: BriefingFacility | undefined): string => {
  const express = facility?.config?.['EXPRESS_SERVICEABILITY'] as { openTime?: unknown } | undefined;
  const openTime = typeof express?.openTime === 'string' ? express.openTime.trim() : '';
  return facility?.expressServiceabilityEnabled === true && HH_MM.test(openTime)
    ? openTime
    : DEFAULT_START_TIME;
};

/** Pushes and briefings cover stores and warehouses only; unknown types are kept. */
export const isBriefingFacility = (facility: BriefingFacility) =>
  !facility.facilityType || HUB_FACILITY_TYPES.includes(facility.facilityType);

/**
 * Login always briefs (a new operator on a shared device). Otherwise once per working day: the
 * first open, first tap or the facility start time, whichever comes first.
 */
export const isBriefingDue = (trigger: BriefingTrigger, lastBriefedAt: number | null, now: number) =>
  trigger === 'login' ||
  trigger === 'manual' ||
  trigger === 'restore' ||
  trigger === 'refresh' ||
  lastBriefedAt === null ||
  lastBriefedAt < shiftDayStart(now);

const toTime = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    // The APIs send epoch seconds for some date fields.
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * The shared due rule (Follow-ups, Today, package cards): the customer's requested date, else the
 * estimate, else the promise, read as an IST day; Express and Try at Home also carry a clock time.
 */
const orderDueState = (pkg: BriefingPackage, now: number) =>
  deliveryScheduleState(
    resolveDeliverySchedule(pkg as unknown as DeliverySchedulePackage),
    dayjs(now),
  );

/** Same skip list as the WIP push: no store purchases, test or digital orders. */
const isWaitingOrder = (pkg: BriefingPackage) =>
  (!pkg.currentStatus || pkg.currentStatus === 'WIP') &&
  pkg.deliveryMode !== 'STORE_PURCHASE' &&
  pkg.isTestOrder !== true &&
  pkg.fulfillmentMode !== 'DIGITAL';

const isWaitingPickup = (pickup: BriefingPickup) =>
  (!pickup.status || OPEN_PICKUP_STATUSES.includes(pickup.status)) &&
  (!pickup.useCase || pickup.useCase === TRY_AT_HOME_USE_CASE) &&
  (pickup.totalItemsPendingToBeScanned === null ||
    pickup.totalItemsPendingToBeScanned === undefined ||
    pickup.totalItemsPendingToBeScanned > 0);

export const buildBriefing = (
  facilityIds: Array<number>,
  facilities: Array<BriefingFacility>,
  packages: Array<BriefingPackage>,
  pickups: Array<BriefingPickup>,
  now: number,
  /** The API reported more rows than were loaded (a full page): `true` for both lists, or per list. */
  more: boolean | { packages?: boolean; pickups?: boolean } = false,
  /** Per SCM-run facility: the orders the viewer can pick now. Other facilities count WIP packages. */
  pickable: Map<number, PickableAtFacility> = new Map(),
): Briefing => {
  const ordersCapped = typeof more === 'boolean' ? more : more.packages === true;
  const pickupsCapped = typeof more === 'boolean' ? more : more.pickups === true;
  const byId = new Map(facilities.map(facility => [facility.id, facility]));
  const endOfToday = endOfIstDay(now);
  const rows = new Map<number, FacilityBacklog>();
  const row = (facilityId: number) => {
    let backlog = rows.get(facilityId);
    if (!backlog) {
      const name = byId.get(facilityId)?.name?.trim();
      backlog = {
        facilityId,
        facilityName: name || `Facility ${facilityId}`,
        orders: 0,
        ordersOverdue: 0,
        ordersDueToday: 0,
        pickups: 0,
        pickupsOverdue: 0,
        pickupsDueToday: 0,
        pickupsUpcoming: 0,
        ...(ordersCapped ? { ordersCapped } : {}),
        ...(pickupsCapped ? { pickupsCapped } : {}),
      };
      rows.set(facilityId, backlog);
    }
    return backlog;
  };
  const inScope = (facilityId: number | null | undefined): facilityId is number => {
    if (facilityId === null || facilityId === undefined || !facilityIds.includes(facilityId)) {
      return false;
    }
    const facility = byId.get(facilityId);
    return !facility || isBriefingFacility(facility);
  };

  packages.filter(isWaitingOrder).forEach(pkg => {
    if (!inScope(pkg.fulfillmentFacilityId)) {
      return;
    }
    // Where SCM picks, a WIP package counts only when it can be picked now (its count is set below).
    const scm = pickable.get(pkg.fulfillmentFacilityId);
    if (scm && !scm.packageIds.has(String(pkg.id))) {
      return;
    }
    const backlog = row(pkg.fulfillmentFacilityId);
    backlog.orders += 1;
    const state = orderDueState(pkg, now);
    if (state === DELIVERY_LABELS.overdue) {
      backlog.ordersOverdue += 1;
    } else if (state === DELIVERY_LABELS.today) {
      backlog.ordersDueToday += 1;
    }
  });

  pickable.forEach((scm, facilityId) => {
    if (!inScope(facilityId) || scm.count === 0) {
      return;
    }
    const backlog = row(facilityId);
    // Every pickable order, including ones beyond the loaded page and those on the viewer's own picklists.
    backlog.orders = scm.count;
    delete backlog.ordersCapped;
    backlog.ordersOverdue = Math.min(backlog.ordersOverdue, scm.count);
    backlog.ordersDueToday = Math.min(backlog.ordersDueToday, scm.count - backlog.ordersOverdue);
  });

  pickups.filter(isWaitingPickup).forEach(pickup => {
    if (!inScope(pickup.fromFacilityId)) {
      return;
    }
    const backlog = row(pickup.fromFacilityId);
    backlog.pickups += 1;
    const scanBy = toTime(pickup.expectedScanDate);
    if (scanBy !== null && scanBy < now) {
      backlog.pickupsOverdue += 1;
    } else if (scanBy !== null && scanBy < endOfToday) {
      backlog.pickupsDueToday += 1;
    } else if (scanBy !== null) {
      backlog.pickupsUpcoming += 1;
    }
  });

  return {
    facilities: [...rows.values()].filter(r => r.orders + r.pickups > 0).sort(
      (a, b) =>
        b.orders + b.pickups - (a.orders + a.pickups) || a.facilityName.localeCompare(b.facilityName),
    ),
    // A full WIP page says nothing about SCM-run facilities: their count is the pickable orders, not the page.
    partial: pickupsCapped || (ordersCapped && facilityIds.some(id => !pickable.has(id))),
    at: now,
  };
};

const plural = (count: number, one: string, many: string, capped = false) =>
  capped ? `${count}+ ${many}` : `${count} ${count === 1 ? one : many}`;

/** "12 online orders", or "50+ online orders" when the list hit the page cap. */
export const ordersLabel = (backlog: FacilityBacklog) =>
  plural(backlog.orders, 'online order', 'online orders', backlog.ordersCapped);

export const pickupsLabel = (backlog: FacilityBacklog) =>
  plural(backlog.pickups, 'Try at Home pickup', 'Try at Home pickups', backlog.pickupsCapped);

/** " · 2 overdue · 1 due today" ("all overdue" when every one is late), as on the in-app card. */
export const overdueCount = (total: number, overdue: number) =>
  overdue === total && total > 1 ? 'all' : String(overdue);

const urgency = (total: number, overdue: number, dueToday: number) =>
  (overdue > 0 ? ` · ${overdueCount(total, overdue)} overdue` : '') +
  (dueToday > 0 ? ` · ${dueToday} due today` : '');

export const ordersLine = (backlog: FacilityBacklog) =>
  backlog.orders === 0
    ? null
    : ordersLabel(backlog) +
      urgency(backlog.orders, backlog.ordersOverdue, backlog.ordersDueToday);

export const pickupsLine = (backlog: FacilityBacklog) =>
  backlog.pickups === 0
    ? null
    : pickupsLabel(backlog) +
      urgency(backlog.pickups, backlog.pickupsOverdue, backlog.pickupsDueToday);

export const briefingTitle = (briefing: Briefing) =>
  briefing.facilities.length === 1
    ? `Waiting at ${briefing.facilities[0].facilityName}`
    : `Waiting at ${briefing.facilities.length} facilities`;

/** One line per facility for the system notification (top 3). */
export const briefingBody = (briefing: Briefing) => {
  const lines = briefing.facilities.slice(0, 3).map(backlog => {
    const parts = [ordersLine(backlog), pickupsLine(backlog)].filter(Boolean).join(', ');
    return briefing.facilities.length === 1 ? parts : `${backlog.facilityName}: ${parts}`;
  });
  if (briefing.facilities.length > 3) {
    lines.push(`+${briefing.facilities.length - 3} more facilities`);
  }
  return lines.join('\n');
};

/** Where "Start online orders" goes: the WIP tab; pickers go to Order Picking. */
export const ordersDestination = (canOpenPackages: boolean) =>
  canOpenPackages ? '/packages?tab=wip' : ORDER_PICKING_PATH;

/** Where "Start pickups" goes: the most urgent outgoing transfers tab. */
export const pickupsDestination = (backlog: FacilityBacklog, canOpenPackages: boolean) => {
  if (!canOpenPackages) {
    return STN_PICKING_PATH;
  }
  // Pickups without a scan-by time are on neither dated tab: open All for them.
  const tab =
    backlog.pickupsOverdue > 0
      ? 'delayed'
      : backlog.pickupsDueToday > 0
        ? 'today'
        : backlog.pickupsUpcoming > 0
          ? 'upcoming'
          : 'all';
  // Only the Try at Home transfers the card counted, not every outgoing transfer.
  return `/outgoing-stock-transfers?tab=${tab}&useCase=${TRY_AT_HOME_USE_CASE}`;
};
